import { queryClient } from '@/api/queryClient';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { useAuth } from '@/store/auth/auth.store';
import { useChats } from '@/store/chats/chats.store';
import { useEvents } from '@/store/events/events.store';
import { useInvoice } from '@/store/expenses-store/invoices/invoices.store';
import { useQuotesStore } from '@/store/expenses-store/quotes/quotes.store';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store';
import { useFoldersStore } from '@/store/folders/folders.store';
import { useMessages } from '@/store/messages/messages.store';
import { useTasks } from '@/store/tasks/tasks.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { useWebSocket } from '@/store/websocket/websocket.store';

import { initActiveAccount } from './current-service-provider.store';

/**
 * Drops what was loaded for the previous service and remounts the route, so each page and
 * widget fetches for the new one on its own and shows its own loading state. The shell holds
 * a spinner only until the new service's rights are in: route guards read them, and a guard
 * that sees the old service's rights redirects in a way it cannot take back. Everything else
 * (the session, the vessel list, the socket) is reloaded behind the page.
 */
export const refreshAfterAccountSwitch = async () => {
  clearServiceData();
  useActiveAccount.setState((state) => ({ switching: true, generation: state.generation + 1 }));
  void reloadInBackground();

  try {
    await useActiveAccount.getState().roleUpdate;
  } finally {
    useActiveAccount.setState({ switching: false });
  }
};

const clearServiceData = () => {
  void queryClient.invalidateQueries();

  useTasks.getState().clearTasks();
  useEvents.getState().clearEvents();
  useChats.setState({ chatRooms: null, chatRoom: null, bids: null, bid: null });
  useInvoice.setState({ invoices: [], invoice: null });
  useQuotesStore.setState({ quotes: null, quote: null });
  // Cursors are bound to the service they were issued under.
  useMessages.setState({
    messages: null,
    bidMessages: null,
    pinnedMessages: null,
    cursorByRoom: {},
  });
  useFoldersStore.setState({ folders: null });
  // The vessel list is the person's, not the service's: it stays, so the active vessel is
  // derived from it at once, and is reloaded below with the session.
  useVessels.setState({ ownedVessels: undefined });
  useTransactionsStore.setState({
    transactions: [],
    transaction: null,
    transactionCategories: [],
  });
};

const reloadInBackground = async () => {
  if ('caches' in window) {
    await Promise.allSettled([caches.delete('capacitor-api-cache'), caches.delete('api-cache')]);
  }

  useWebSocket.getState().disconnect();
  useWebSocket.getState().connect();

  try {
    await useAuth.getState().me();
    const services = useAuth.getState().session?.services;
    if (services) await initActiveAccount(services);
  } catch (error) {
    console.error('Failed to reload the session after an account switch:', error);
  }
};
